import * as E from 'fp-ts/lib/Either'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'
import * as R from 'ramda'

import { AttachmentError } from './attachment-error'

import {
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileWord,
  faImage,
  IconDefinition
} from '@fortawesome/pro-light-svg-icons'

class FileSupport {
  constructor(
    readonly ext: SupportedFileExtension,
    readonly icon: IconDefinition,
    readonly mimeType: string,
    readonly mimeSignature: number[]
  ) {}
}

class ValidFile {
  constructor(
    readonly file: File,
    readonly mimeType: string
  ) {}
}

export type SupportedFileExtension =
  | '.doc'
  | '.docx'
  | '.jpeg'
  | '.jpg'
  | '.pdf'
  | '.png'
  | '.ppt'
  | '.pptx'
  | '.xls'
  | '.xlsx'

const mimeByExt: Record<SupportedFileExtension, string> = {
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

// https://en.wikipedia.org/wiki/List_of_file_signatures
// https://github.com/sindresorhus/file-type
// tslint:disable-next-line:max-line-length
const cfb = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] // https://social.msdn.microsoft.com/Forums/en-US/343d09e3-5fdf-4b4a-9fa6-8ccb37a35930/developing-a-tool-to-recognise-ms-office-file-types-doc-xls-mdb-ppt-?forum=os_binaryfile
const jpeg = [0xff, 0xd8, 0xff]
const nonEmptyZip = [0x50, 0x4b, 0x03, 0x04]
const bytesByExt: Record<SupportedFileExtension, number[]> = {
  '.doc': cfb,
  '.docx': nonEmptyZip,
  '.jpeg': jpeg,
  '.jpg': jpeg,
  '.pdf': [0x25, 0x50, 0x44, 0x46],
  '.png': [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  '.ppt': cfb,
  '.pptx': nonEmptyZip,
  '.xls': cfb,
  '.xlsx': nonEmptyZip
}

export class AttachmentSupport {
  private static readonly all: ReadonlyArray<FileSupport> = [
    new FileSupport('.doc', faFileWord, mimeByExt['.doc'], bytesByExt['.doc']),
    new FileSupport(
      '.docx',
      faFileWord,
      mimeByExt['.docx'],
      bytesByExt['.docx']
    ),
    new FileSupport(
      '.jpeg',
      faFileImage,
      mimeByExt['.jpeg'],
      bytesByExt['.jpeg']
    ),
    new FileSupport('.jpg', faFileImage, mimeByExt['.jpg'], bytesByExt['.jpg']),
    new FileSupport('.pdf', faFilePdf, mimeByExt['.pdf'], bytesByExt['.pdf']),
    new FileSupport('.png', faFileImage, mimeByExt['.png'], bytesByExt['.png']),
    new FileSupport(
      '.ppt',
      faFilePowerpoint,
      mimeByExt['.ppt'],
      bytesByExt['.ppt']
    ),
    new FileSupport(
      '.pptx',
      faFilePowerpoint,
      mimeByExt['.pptx'],
      bytesByExt['.pptx']
    ),
    new FileSupport('.xls', faFileExcel, mimeByExt['.xls'], bytesByExt['.xls']),
    new FileSupport(
      '.xlsx',
      faFileExcel,
      mimeByExt['.xlsx'],
      bytesByExt['.xlsx']
    )
  ]
  private static readonly byExt: Record<string, FileSupport> = R.indexBy(
    (x) => x.ext,
    AttachmentSupport.all
  )
  private static readonly maxFileSizeBytes = 10 * Math.pow(2, 20) // 10M

  public static readonly extensions: ReadonlyArray<string> =
    AttachmentSupport.all.map((x) => x.ext)

  public static getFileIcon(filename: string): IconDefinition {
    return pipe(
      extensionOf(filename),
      O.fold(
        () => faImage,
        (x) => {
          const candidate: FileSupport | undefined = this.byExt[x.toLowerCase()]
          return (candidate && candidate.icon) || faImage
        }
      )
    )
  }

  public static async checkIfSupported(
    file: File
  ): Promise<E.Either<AttachmentError.Type, ValidFile>> {
    if (file.size > this.maxFileSizeBytes) {
      return E.left(new AttachmentError.FileTooBig(file))
    }

    const maybeValidExtension: E.Either<
      AttachmentError.Type,
      SupportedFileExtension
    > = pipe(
      extensionOf(file.name),
      E.fromOption<AttachmentError.Type>(
        () => new AttachmentError.MissingExtension(file)
      ),
      E.chain((ext) => {
        const candidate = ext.toLowerCase()
        if (this.extensions.indexOf(candidate) === -1) {
          return E.left(new AttachmentError.InvalidExtension(file))
        }
        return E.right(candidate as SupportedFileExtension)
      })
    )

    return pipe(
      maybeValidExtension,
      E.fold<
        AttachmentError.Type,
        SupportedFileExtension,
        Promise<E.Either<AttachmentError.Type, ValidFile>>
      >(
        async (l) => E.left(l),
        async (a) => {
          const expectedMimeSignature = this.byExt[a].mimeSignature
          const mimeValid = await checkMimeSignature(
            file,
            expectedMimeSignature
          )
          if (!mimeValid) {
            return E.left(new AttachmentError.InvalidMimeType(file))
          }
          return E.right(new ValidFile(file, mimeByExt[a]))
        }
      )
    )
  }
}

function checkMimeSignature(
  file: File,
  expectedSignature: number[]
): Promise<boolean> {
  if (file.size < expectedSignature.length) {
    return Promise.resolve(false)
  }

  return new Promise((resolve) => {
    read((data) => {
      // tslint:disable:no-increment-decrement
      for (let i = 0; i < expectedSignature.length; i++) {
        if (data[i] !== expectedSignature[i]) {
          return resolve(false)
        }
      }
      return resolve(true)
    })
  })

  function read(callback: (data: Uint8Array) => void) {
    const reader = new FileReader()
    reader.onload = () => callback(new Uint8Array(reader.result as any))
    reader.readAsArrayBuffer(file.slice(0, expectedSignature.length))
  }
}

export function extensionOf(filename: string): O.Option<string> {
  if (!hasExtension(filename)) {
    return O.none
  }
  return O.some(filename.substring(filename.lastIndexOf('.')))
}

export function generateUniqueName(filenames: string[], candidate: string) {
  const candidateBody = withoutExtension(candidate)
  const candidateExt = extensionOf(candidate)

  const reDuplicates = pipe(
    candidateExt,
    O.fold(
      () => new RegExp(`^${candidateBody} ?(\\([0-9]+\\))?$`, 'i'),
      (x) => new RegExp(`^${candidateBody} ?(\\([0-9]+\\))?\\${x}$`, 'i')
    )
  )

  const duplicates = filenames
    .filter((x) => !!x.match(reDuplicates))
    .map(withoutExtension)

  if (duplicates.length === 0) {
    return candidate
  }

  const reDuplicateFileIdx = /\(([0-9]+)\)$/

  const incrementId = duplicates.reduce((highestIncrementId, currentValue) => {
    const n = currentValue.match(reDuplicateFileIdx)

    if (!n) {
      return highestIncrementId
    }

    const candicate = parseInt(n[1], 10)
    return candicate > highestIncrementId ? candicate : highestIncrementId
  }, 0)

  return `${candidateBody} (${incrementId + 1})${O.getOrElse(() => '')(
    candidateExt
  )}`
}

function withoutExtension(filename: string) {
  if (!hasExtension(filename)) {
    return filename
  }
  return filename.substring(0, filename.lastIndexOf('.'))
}

function hasExtension(filename: string) {
  const re = /[^\.]\.[^\.]+$/
  return filename.match(re)
}
